import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getCardImageSrc, handleCardImageError } from '../hooks/useCardImage';
import { getCardRarityClass } from '../utils/cardUtils';
import CardPreviewModal from './CardPreviewModal';

const RewardDisplay = ({ reward, isClaimed }) => {
    const [showCardModal, setShowCardModal] = useState(false);

    if (!reward) return null;

    const hasPack = reward.seriesID && reward.Series;
    const hasCard = reward.cardID && reward.Card;
    const hasMoney = reward.money && Number(reward.money) > 0;
    const variationName = reward.CardVariation?.name;

    if (!hasPack && !hasCard && !hasMoney) return null;

    const packTooltip = hasPack
        ? `${reward.numberOfSeries || 1}x ${reward.Series.name} packs`
        : '';

    const variationLabel = variationName ? `${variationName} or Rarer` : null;

    const cardTooltip = hasCard
        ? `[${reward.Card.rating}] ${variationLabel ? variationLabel + ' ' : ''}${reward.Card.year} ${reward.Card.name}`
        : '';

    // Card image: use customImage, or resolve from the card's first Series
    const cardImageSrc = hasCard
        ? (reward.Card.customImage || getCardImageSrc(reward.Card.Series?.[0]?.seriesKey))
        : null;

    return (
        <div className={`reward-display${isClaimed ? ' reward-display--claimed' : ''}`}>
            <div className="reward-display-label">Rewards</div>
            <div className="reward-display-items">
                {hasPack && (
                    <div className="reward-display-item reward-display-has-tooltip" data-tooltip={packTooltip}>
                        <div className="reward-display-img-wrapper">
                            <img
                                src={getCardImageSrc(reward.Series.seriesKey)}
                                alt={reward.Series.name}
                                className="reward-display-pack-img"
                                onError={handleCardImageError}
                            />
                            {reward.numberOfSeries > 1 && (
                                <span className="reward-display-multiplier">x{reward.numberOfSeries}</span>
                            )}
                        </div>
                        {reward.seriesPct != null && reward.seriesPct < 100 && (
                            <span className="reward-display-pct">{reward.seriesPct}%</span>
                        )}
                    </div>
                )}

                {hasCard && (
                    <div
                        className="reward-display-item reward-display-has-tooltip reward-display-clickable"
                        data-tooltip={`${cardTooltip} — click to preview`}
                        onClick={() => setShowCardModal(true)}
                    >
                        <div className="reward-display-img-wrapper reward-display-card-wrapper">
                            <img
                                src={cardImageSrc}
                                alt={reward.Card.name}
                                className="reward-display-card-img"
                                onError={handleCardImageError}
                            />
                            <span className={`reward-display-card-rating ${getCardRarityClass(reward.Card.rating)}`}>
                                {reward.Card.rating}
                            </span>
                        </div>
                        {variationLabel && (
                            <span className="reward-display-variation-label">{variationLabel}</span>
                        )}
                        {!isClaimed && reward.cardPct != null && reward.cardPct < 100 && (
                            <span className="reward-display-pct">{reward.cardPct}%</span>
                        )}
                    </div>
                )}

                {hasMoney && (
                    <div className="reward-display-item">
                        <span className="reward-display-money">${Number(reward.money).toLocaleString()}</span>
                    </div>
                )}
            </div>
            {isClaimed && (
                <div className="reward-display-claimed">Rewards already claimed</div>
            )}
            {showCardModal && hasCard && (
                <CardPreviewModal card={reward.Card} onClose={() => setShowCardModal(false)} />
            )}
        </div>
    );
};

RewardDisplay.propTypes = {
    isClaimed: PropTypes.bool,
    reward: PropTypes.shape({
        money: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        seriesID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        numberOfSeries: PropTypes.number,
        cardID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        seriesPct: PropTypes.number,
        cardPct: PropTypes.number,
        Series: PropTypes.shape({
            seriesKey: PropTypes.string,
            name: PropTypes.string,
        }),
        Card: PropTypes.shape({
            customImage: PropTypes.string,
            name: PropTypes.string,
            year: PropTypes.number,
            rating: PropTypes.number,
            Series: PropTypes.arrayOf(PropTypes.shape({
                seriesKey: PropTypes.string,
            })),
        }),
    }),
};

export default RewardDisplay;
